import React, { useState } from 'react';
import Header from '../components/landing/Header';
import Footer from '../components/landing/Footer';
import { contactInfo, restaurantInfo } from '../data/mock';
import { Calendar, Clock, Users, Phone, User, MessageSquare, CheckCircle, AlertCircle } from 'lucide-react';
import { useSEO } from '../utils/seo';

const ReservationPage = () => {
    useSEO('contact'); // Using contact SEO for now

    const [formData, setFormData] = useState({
        name: '',
        phone: '',
        email: '',
        date: '',
        time: '',
        guests: '2',
        occasion: '',
        specialRequests: ''
    });

    const [isSubmitted, setIsSubmitted] = useState(false);
    const [errors, setErrors] = useState({});

    const occasions = [
        'Birthday',
        'Anniversary',
        'Business Meeting',
        'Date Night',
        'Family Gathering',
        'Other'
    ];

    const timeSlots = [
        '10:00 AM', '10:30 AM', '11:00 AM', '11:30 AM',
        '12:00 PM', '12:30 PM', '1:00 PM', '1:30 PM',
        '2:00 PM', '2:30 PM', '3:00 PM', '3:30 PM',
        '4:00 PM', '4:30 PM', '5:00 PM', '5:30 PM',
        '6:00 PM', '6:30 PM', '7:00 PM', '7:30 PM',
        '8:00 PM', '8:30 PM', '9:00 PM', '9:30 PM',
        '10:00 PM', '10:30 PM', '11:00 PM', '11:30 PM',
        '12:00 AM', '12:30 AM'
    ];

    const guestOptions = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10+'];

    const validateForm = () => {
        const newErrors = {};

        if (!formData.name.trim()) {
            newErrors.name = 'Name is required';
        }

        if (!formData.phone.trim()) {
            newErrors.phone = 'Phone number is required';
        } else if (!/^[0-9]{10}$/.test(formData.phone.replace(/\s/g, ''))) {
            newErrors.phone = 'Please enter a valid 10-digit phone number';
        }

        if (!formData.date) {
            newErrors.date = 'Please select a date';
        } else {
            const selectedDate = new Date(formData.date);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            if (selectedDate < today) {
                newErrors.date = 'Please select a future date';
            }
        }

        if (!formData.time) {
            newErrors.time = 'Please select a time';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (validateForm()) {
            // In a real app, this would send data to a backend
            console.log('Reservation submitted:', formData);
            setIsSubmitted(true);

            // Reset form after 5 seconds
            setTimeout(() => {
                setIsSubmitted(false);
                setFormData({
                    name: '',
                    phone: '',
                    email: '',
                    date: '',
                    time: '',
                    guests: '2',
                    occasion: '',
                    specialRequests: ''
                });
            }, 10000);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    // Get today's date in YYYY-MM-DD format for min date
    const today = new Date().toISOString().split('T')[0];

    if (isSubmitted) {
        return (
            <div className="min-h-screen">
                <Header />
                <main className="pt-20">
                    <section className="section-padding bg-[var(--bg-primary)] min-h-[70vh] flex items-center justify-center">
                        <div className="container">
                            <div className="max-w-lg mx-auto text-center">
                                <div className="w-20 h-20 mx-auto mb-6 bg-green-100 rounded-full flex items-center justify-center">
                                    <CheckCircle size={48} className="text-green-600" />
                                </div>
                                <h1 className="heading-1 mb-4">Reservation Confirmed!</h1>
                                <p className="body-large text-[var(--text-secondary)] mb-6">
                                    Thank you, <strong>{formData.name}</strong>! Your table has been reserved.
                                </p>
                                <div className="bg-[var(--bg-secondary)] p-6 mb-8 text-left">
                                    <h3 className="heading-3 mb-4">Reservation Details</h3>
                                    <div className="space-y-3 text-sm">
                                        <div className="flex justify-between">
                                            <span className="text-[var(--text-secondary)]">Date:</span>
                                            <span className="font-medium">{new Date(formData.date).toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-[var(--text-secondary)]">Time:</span>
                                            <span className="font-medium">{formData.time}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-[var(--text-secondary)]">Guests:</span>
                                            <span className="font-medium">{formData.guests} {parseInt(formData.guests) === 1 ? 'person' : 'people'}</span>
                                        </div>
                                        {formData.occasion && (
                                            <div className="flex justify-between">
                                                <span className="text-[var(--text-secondary)]">Occasion:</span>
                                                <span className="font-medium">{formData.occasion}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <p className="body-small text-[var(--text-secondary)] mb-6">
                                    A confirmation message will be sent to <strong>{formData.phone}</strong>
                                </p>
                                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                                    <a href={contactInfo.phoneLink} className="btn-primary-filled">
                                        <Phone size={18} />
                                        Call Restaurant
                                    </a>
                                    <a href="/" className="btn-primary">
                                        Back to Home
                                    </a>
                                </div>
                            </div>
                        </div>
                    </section>
                </main>
                <Footer />
            </div>
        );
    }

    return (
        <div className="min-h-screen">
            <Header />
            <main className="pt-20">
                {/* Hero */}
                <section className="section-padding bg-[var(--bg-secondary)]">
                    <div className="container">
                        <div className="max-w-3xl mx-auto text-center">
                            <h1 className="hero-large mb-6">Reserve Your Table</h1>
                            <p className="body-large text-[var(--text-secondary)]">
                                Book your dining experience at White Valley Cafe. We look forward to serving you!
                            </p>
                        </div>
                    </div>
                </section>

                {/* Reservation Form */}
                <section className="section-padding bg-[var(--bg-primary)]">
                    <div className="container">
                        <div className="max-w-2xl mx-auto">
                            <form onSubmit={handleSubmit} className="space-y-6">
                                {/* Personal Info */}
                                <div className="bg-[var(--bg-secondary)] p-6 md:p-8">
                                    <h2 className="heading-2 mb-6 flex items-center gap-2">
                                        <User size={24} />
                                        Your Details
                                    </h2>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium mb-2" htmlFor="name">
                                                Full Name *
                                            </label>
                                            <input
                                                type="text"
                                                id="name"
                                                name="name"
                                                value={formData.name}
                                                onChange={handleChange}
                                                className={`w-full px-4 py-3 border ${errors.name ? 'border-red-500' : 'border-[var(--border-light)]'} bg-[var(--bg-primary)] focus:outline-none focus:border-[var(--text-primary)]`}
                                                placeholder="Enter your name"
                                            />
                                            {errors.name && (
                                                <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                                                    <AlertCircle size={12} /> {errors.name}
                                                </p>
                                            )}
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium mb-2" htmlFor="phone">
                                                Phone Number *
                                            </label>
                                            <input
                                                type="tel"
                                                id="phone"
                                                name="phone"
                                                value={formData.phone}
                                                onChange={handleChange}
                                                className={`w-full px-4 py-3 border ${errors.phone ? 'border-red-500' : 'border-[var(--border-light)]'} bg-[var(--bg-primary)] focus:outline-none focus:border-[var(--text-primary)]`}
                                                placeholder="10-digit mobile number"
                                            />
                                            {errors.phone && (
                                                <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                                                    <AlertCircle size={12} /> {errors.phone}
                                                </p>
                                            )}
                                        </div>
                                    </div>

                                    <div className="mt-4">
                                        <label className="block text-sm font-medium mb-2" htmlFor="email">
                                            Email (Optional)
                                        </label>
                                        <input
                                            type="email"
                                            id="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleChange}
                                            className="w-full px-4 py-3 border border-[var(--border-light)] bg-[var(--bg-primary)] focus:outline-none focus:border-[var(--text-primary)]"
                                            placeholder="email@example.com"
                                        />
                                    </div>
                                </div>

                                {/* Reservation Details */}
                                <div className="bg-[var(--bg-secondary)] p-6 md:p-8">
                                    <h2 className="heading-2 mb-6 flex items-center gap-2">
                                        <Calendar size={24} />
                                        Reservation Details
                                    </h2>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium mb-2" htmlFor="date">
                                                Date *
                                            </label>
                                            <input
                                                type="date"
                                                id="date"
                                                name="date"
                                                value={formData.date}
                                                onChange={handleChange}
                                                min={today}
                                                className={`w-full px-4 py-3 border ${errors.date ? 'border-red-500' : 'border-[var(--border-light)]'} bg-[var(--bg-primary)] focus:outline-none focus:border-[var(--text-primary)]`}
                                            />
                                            {errors.date && (
                                                <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                                                    <AlertCircle size={12} /> {errors.date}
                                                </p>
                                            )}
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium mb-2" htmlFor="time">
                                                Time *
                                            </label>
                                            <select
                                                id="time"
                                                name="time"
                                                value={formData.time}
                                                onChange={handleChange}
                                                className={`w-full px-4 py-3 border ${errors.time ? 'border-red-500' : 'border-[var(--border-light)]'} bg-[var(--bg-primary)] focus:outline-none focus:border-[var(--text-primary)]`}
                                            >
                                                <option value="">Select time</option>
                                                {timeSlots.map(slot => (
                                                    <option key={slot} value={slot}>{slot}</option>
                                                ))}
                                            </select>
                                            {errors.time && (
                                                <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                                                    <AlertCircle size={12} /> {errors.time}
                                                </p>
                                            )}
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                                        <div>
                                            <label className="block text-sm font-medium mb-2" htmlFor="guests">
                                                Number of Guests
                                            </label>
                                            <div className="flex items-center gap-2">
                                                <Users size={20} className="text-[var(--text-secondary)]" />
                                                <select
                                                    id="guests"
                                                    name="guests"
                                                    value={formData.guests}
                                                    onChange={handleChange}
                                                    className="flex-1 px-4 py-3 border border-[var(--border-light)] bg-[var(--bg-primary)] focus:outline-none focus:border-[var(--text-primary)]"
                                                >
                                                    {guestOptions.map(num => (
                                                        <option key={num} value={num}>{num} {num === '1' ? 'Guest' : 'Guests'}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium mb-2" htmlFor="occasion">
                                                Occasion (Optional)
                                            </label>
                                            <select
                                                id="occasion"
                                                name="occasion"
                                                value={formData.occasion}
                                                onChange={handleChange}
                                                className="w-full px-4 py-3 border border-[var(--border-light)] bg-[var(--bg-primary)] focus:outline-none focus:border-[var(--text-primary)]"
                                            >
                                                <option value="">Select occasion</option>
                                                {occasions.map(occ => (
                                                    <option key={occ} value={occ}>{occ}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                </div>

                                {/* Special Requests */}
                                <div className="bg-[var(--bg-secondary)] p-6 md:p-8">
                                    <h2 className="heading-2 mb-6 flex items-center gap-2">
                                        <MessageSquare size={24} />
                                        Special Requests
                                    </h2>

                                    <textarea
                                        id="specialRequests"
                                        name="specialRequests"
                                        value={formData.specialRequests}
                                        onChange={handleChange}
                                        rows={4}
                                        className="w-full px-4 py-3 border border-[var(--border-light)] bg-[var(--bg-primary)] focus:outline-none focus:border-[var(--text-primary)] resize-none"
                                        placeholder="Any dietary requirements, seating preferences, or special requests..."
                                    />
                                </div>

                                {/* Submit */}
                                <div className="text-center">
                                    <button
                                        type="submit"
                                        className="btn-primary-filled text-lg px-12 py-4"
                                    >
                                        Confirm Reservation
                                    </button>
                                    <p className="body-small text-[var(--text-secondary)] mt-4">
                                        By reserving, you agree to our reservation policy. For large groups (10+), please call us directly.
                                    </p>
                                </div>
                            </form>

                            {/* Contact Alternative */}
                            <div className="mt-12 text-center border-t border-[var(--border-light)] pt-8">
                                <p className="body-regular text-[var(--text-secondary)] mb-4">
                                    Prefer to book over the phone?
                                </p>
                                <a href={contactInfo.phoneLink} className="btn-primary">
                                    <Phone size={18} />
                                    Call {contactInfo.phone}
                                </a>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Info Section */}
                <section className="section-padding-small bg-[var(--bg-secondary)]">
                    <div className="container">
                        <div className="max-w-3xl mx-auto">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
                                <div>
                                    <Clock size={28} className="mx-auto mb-3 text-[var(--text-primary)]" />
                                    <h3 className="heading-3 mb-2">Opening Hours</h3>
                                    <p className="body-small text-[var(--text-secondary)]">Daily 10 AM – 1 AM</p>
                                </div>
                                <div>
                                    <Users size={28} className="mx-auto mb-3 text-[var(--text-primary)]" />
                                    <h3 className="heading-3 mb-2">Group Bookings</h3>
                                    <p className="body-small text-[var(--text-secondary)]">For 10+ guests, call us</p>
                                </div>
                                <div>
                                    <Calendar size={28} className="mx-auto mb-3 text-[var(--text-primary)]" />
                                    <h3 className="heading-3 mb-2">Advance Booking</h3>
                                    <p className="body-small text-[var(--text-secondary)]">Up to 30 days ahead</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </main>
            <Footer />
        </div>
    );
};

export default ReservationPage;
